import React, { useState } from "react";
import {
  FileSpreadsheet,
  Download,
  Upload,
  CheckCircle2,
  AlertCircle,
  X,
  Loader2,
  FileText,
  Trash2,
} from "lucide-react";
import { downloadExcelTemplate, parseExcelFile } from "@/lib/excelUtils";
import { useToast } from "@/hooks/use-toast";

/**
 * Bulk Upload Modal Component for Excel Uploads
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {"members" | "donors" | "volunteers"} props.entityType
 * @param {(items: Array<Object>) => Promise<Object>} props.onUpload
 * @param {() => void} [props.onSuccess]
 */
export default function BulkUploadModal({
  isOpen,
  onClose,
  entityType,
  onUpload,
  onSuccess,
}) {
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  if (!isOpen) return null;

  const entityTitle =
    entityType === "members"
      ? "Members / Users"
      : entityType === "donors"
      ? "Donors"
      : "Volunteers";

  const handleFileChange = async (selectedFile) => {
    if (!selectedFile) return;
    const name = selectedFile.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls") && !name.endsWith(".csv")) {
      toast({
        title: "Invalid file type",
        description: "Please select a valid Excel (.xlsx, .xls) or CSV file.",
        variant: "destructive",
      });
      return;
    }

    setFile(selectedFile);
    setParsing(true);
    setUploadResult(null);

    try {
      const rows = await parseExcelFile(selectedFile);
      setParsedRows(rows);
    } catch (err) {
      toast({
        title: "Parsing error",
        description: err?.message || "Failed to read file",
        variant: "destructive",
      });
      setFile(null);
      setParsedRows([]);
    } finally {
      setParsing(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const resetFile = () => {
    setFile(null);
    setParsedRows([]);
    setUploadResult(null);
  };

  const handleDownloadTemplate = () => {
    downloadExcelTemplate(entityType);
    toast({
      title: "Template downloaded",
      description: `Sample template for ${entityTitle} saved to downloads.`,
    });
  };

  const handleSubmit = async () => {
    if (!parsedRows || parsedRows.length === 0) {
      toast({
        title: "No data to upload",
        description: "The selected file is empty.",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const result = await onUpload(parsedRows);
      setUploadResult(result);
      toast({
        title: "Bulk upload complete",
        description: `Successfully processed ${result.successCount} of ${result.total} records.`,
      });
      if (onSuccess) onSuccess();
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err?.response?.data?.detail || err?.message || "Error processing bulk upload",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  // Row field inspector for visual preview
  const getRowMainField = (row) => {
    if (entityType === "members") {
      return {
        name: row["Full Name"] || row.full_name || row.Name || row.name || "—",
        email: row["Email Address"] || row.email || row.Email || "—",
        detail: row["Role"] || row.role || "volunteer",
      };
    }
    if (entityType === "donors") {
      return {
        name: row["Full Name"] || row.full_name || row.Name || row.name || "—",
        email: row["Email Address"] || row.email || row.Email || "—",
        detail: row["Tags"] || row.tags || "—",
      };
    }
    return {
      name: row["Full Name"] || row.full_name || row.Name || row.name || "—",
      email: row["Email Address"] || row.email || row.Email || "—",
      detail: row["Skills"] || row.skills || "—",
    };
  };

  const isRowValid = (row) => {
    const main = getRowMainField(row);
    if (entityType === "members") {
      return Boolean(main.name !== "—" && main.email !== "—" && String(main.email).includes("@"));
    }
    return Boolean(main.name !== "—");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-accent/20">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-lg leading-tight">
                Bulk Upload {entityTitle} via Excel
              </h3>
              <p className="text-xs text-muted-foreground">
                Upload `.xlsx`, `.xls`, or `.csv` spreadsheet files to insert multiple records at once.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* Top Actions: Template Download */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium">Need the standard spreadsheet template?</div>
                <div className="text-xs text-muted-foreground">
                  Download our formatted Excel template with pre-filled sample columns.
                </div>
              </div>
            </div>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors border border-border"
            >
              <Download className="h-3.5 w-3.5" /> Download Template
            </button>
          </div>

          {/* Upload Results View if completed */}
          {uploadResult ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg border border-border bg-card text-center">
                  <div className="text-2xl font-bold text-foreground">{uploadResult.total}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Total Processed</div>
                </div>
                <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-center">
                  <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {uploadResult.successCount}
                  </div>
                  <div className="text-xs text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mt-1">
                    Successfully Added
                  </div>
                </div>
                <div className="p-4 rounded-lg border border-rose-500/30 bg-rose-500/10 text-center">
                  <div className="text-2xl font-bold text-rose-600 dark:text-rose-400">
                    {uploadResult.failureCount}
                  </div>
                  <div className="text-xs text-rose-600 dark:text-rose-400 uppercase tracking-wider mt-1">
                    Failed / Skipped
                  </div>
                </div>
              </div>

              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="border border-rose-500/20 rounded-lg bg-rose-500/5 p-4 space-y-2 max-h-48 overflow-y-auto">
                  <div className="text-xs font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wider">
                    Error Log ({uploadResult.errors.length} rows failed)
                  </div>
                  <div className="divide-y divide-rose-500/10 text-xs">
                    {uploadResult.errors.map((err, idx) => (
                      <div key={idx} className="py-1.5 flex items-center justify-between text-muted-foreground">
                        <span className="font-mono text-foreground font-medium">Row {err.row}:</span>
                        <span>{err.email || err.name || "Row item"}</span>
                        <span className="text-rose-500 font-medium">{err.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : !file ? (
            /* File Drag & Drop Dropzone */
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center flex flex-col items-center justify-center transition-colors cursor-pointer ${
                dragActive
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50 bg-card"
              }`}
            >
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-3">
                <Upload className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-foreground">
                Click or drag & drop your Excel file here
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Supports .xlsx, .xls, or .csv up to 10MB
              </p>

              <label className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer">
                Browse Files
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => handleFileChange(e.target.files?.[0])}
                />
              </label>
            </div>
          ) : (
            /* File Preview & Row Validation Table */
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-accent/30">
                <div className="flex items-center gap-2 truncate">
                  <FileSpreadsheet className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-xs font-medium truncate">{file.name}</span>
                  <span className="text-[11px] text-muted-foreground font-mono">
                    ({parsedRows.length} rows)
                  </span>
                </div>
                <button
                  onClick={resetFile}
                  disabled={uploading}
                  className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                  title="Remove file"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {parsing ? (
                <div className="py-12 text-center text-sm text-muted-foreground space-y-2">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                  <div>Reading spreadsheet contents...</div>
                </div>
              ) : (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-accent/80 backdrop-blur-sm text-[10px] uppercase tracking-wider text-muted-foreground font-medium border-b border-border">
                        <tr>
                          <th className="px-3 py-2 text-left w-12">Row</th>
                          <th className="px-3 py-2 text-left">Full Name</th>
                          <th className="px-3 py-2 text-left">Email / Contact</th>
                          <th className="px-3 py-2 text-left">Role / Tags / Skills</th>
                          <th className="px-3 py-2 text-right">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {parsedRows.slice(0, 100).map((row, idx) => {
                          const main = getRowMainField(row);
                          const valid = isRowValid(row);
                          return (
                            <tr key={idx} className="hover:bg-accent/20">
                              <td className="px-3 py-2 font-mono text-muted-foreground">{idx + 1}</td>
                              <td className="px-3 py-2 font-medium">{main.name}</td>
                              <td className="px-3 py-2 text-muted-foreground truncate">{main.email}</td>
                              <td className="px-3 py-2 text-muted-foreground truncate">{main.detail}</td>
                              <td className="px-3 py-2 text-right">
                                {valid ? (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                    <CheckCircle2 className="h-3 w-3" /> Ready
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-500/10 text-rose-600 dark:text-rose-400">
                                    <AlertCircle className="h-3 w-3" /> Missing Name/Email
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {parsedRows.length > 100 && (
                    <div className="px-3 py-1.5 text-[11px] text-muted-foreground text-center bg-accent/30 border-t border-border">
                      Showing first 100 of {parsedRows.length} rows
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-accent/10">
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-2 text-xs font-medium rounded-md border border-border bg-background hover:bg-accent transition-colors disabled:opacity-50"
          >
            {uploadResult ? "Close" : "Cancel"}
          </button>

          {uploadResult ? (
            <button
              onClick={() => {
                setUploadResult(null);
                setFile(null);
                setParsedRows([]);
                onClose();
              }}
              className="px-5 py-2 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Done
            </button>
          ) : file && parsedRows.length > 0 ? (
            <button
              onClick={handleSubmit}
              disabled={uploading || parsing}
              className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-3.5 w-3.5" /> Upload {parsedRows.length} Records
                </>
              )}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
